import React from 'react';
import { motion } from 'framer-motion';
import { ReceiptIcon, UsersIcon, UserCheckIcon, ArrowRightLeftIcon, CheckCircle2, CoinsIcon, WalletIcon, HistoryIcon } from 'lucide-react';
const outcomes = [{
  icon: UsersIcon,
  title: 'Share with the right people',
  description: 'Pick exactly who a spend is for, then split it equally or by custom amount.'
}, {
  icon: UserCheckIcon,
  title: 'Whoever actually paid',
  description: 'Log a spend for any member, not just yourself, so the kitty always matches real life.'
}, {
  icon: ArrowRightLeftIcon,
  title: 'Who owes who',
  description: 'A simple way for everyone to settle up, with a breakdown behind each amount.'
}, {
  icon: CheckCircle2,
  title: 'Paid and confirmed',
  description: 'Mark a payment as sent by bank or cash, and the person receiving it confirms it landed.'
}, {
  icon: ReceiptIcon,
  title: 'Receipts',
  description: 'Attach a receipt to an expense so the group has a record of what was bought.'
}, {
  icon: CoinsIcon,
  title: 'More than one currency',
  description: 'Record spends in different currencies when your trip crosses a border.'
}, {
  icon: WalletIcon,
  title: 'My Balance',
  description: 'What you owe and what you are owed across every kitty, open or closed.'
}, {
  icon: HistoryIcon,
  title: 'Full history',
  description: 'Every spend and every settlement record, kept for the whole group to see.'
}];
const alsoIncluded = ['Take part from your browser', 'Admin tools and join requests', 'Add people by name to claim later', 'Optional saved bank details', 'Push notifications', 'Closed kitties keep their balances'];
const containerVariants = {
  hidden: {},
  visible: {
    transition: {
      staggerChildren: 0.06
    }
  }
};
const itemVariants = {
  hidden: {
    opacity: 0,
    y: 24
  },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.5,
      ease: 'easeOut'
    }
  }
};
export function ProductDeepDive() {
  return <section id="features" className="py-24 lg:py-32 bg-gray-950 relative overflow-hidden" data-id="element-247" data-mp="2cabfabhjlk2t">
      {/* Subtle radial glow */}
      <div className="absolute inset-0 pointer-events-none" data-id="element-248" data-mp="2ma2cikxhmd6l">
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[600px] bg-kitty-yellow/5 rounded-full blur-3xl" data-id="element-249" data-mp="2m9aphyhyhnrx" />
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10" data-id="element-250" data-mp="1tkmyrnefu66z">
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true,
        margin: '-80px'
      }} transition={{
        duration: 0.5
      }} className="text-center max-w-2xl mx-auto mb-16 lg:mb-20" data-id="element-251" data-mp="1ykrgthuzv8zb">
          <p className="text-sm font-semibold text-kitty-yellow uppercase tracking-widest mb-4" data-id="element-252" data-mp="15tzoivh1v4h9">
            Features
          </p>
          <h2 className="text-3xl md:text-5xl font-extrabold text-white mb-5 tracking-tight leading-tight" data-id="element-253" data-mp="13bxnb2elm6ze">
            Everything the group needs, in one kitty.
          </h2>
          <p className="text-lg text-gray-400 max-w-lg mx-auto leading-relaxed" data-id="element-254" data-mp="td82p1vbi8fg">
            From the first round to the final settle-up.
          </p>
        </motion.div>

        <motion.div variants={containerVariants} initial="hidden" whileInView="visible" viewport={{
        once: true,
        margin: '-60px'
      }} className="grid md:grid-cols-2 lg:grid-cols-4 gap-6" data-id="element-255" data-mp="odvikovtzfgk">
          {outcomes.map(item => <motion.div key={item.title} variants={itemVariants} className="rounded-2xl border border-gray-800 bg-gray-900/60 backdrop-blur-sm p-6 lg:p-8 group" data-id="element-256" data-mp="ani74cjqdimh">
              <div className="w-12 h-12 rounded-xl bg-kitty-yellow/10 flex items-center justify-center mb-5 group-hover:bg-kitty-yellow/20 transition-colors duration-300" data-id="element-257" data-mp="nrhao647rsq">
                <item.icon className="w-6 h-6 text-kitty-yellow" aria-hidden="true" data-id="element-258" data-mp="lewo1300rrbp" />
              </div>
              <h3 className="text-lg font-bold text-white mb-2 leading-snug" data-id="element-259" data-mp="cljm5bjz9fdh">
                {item.title}
              </h3>
              <p className="text-sm text-gray-400 leading-relaxed" data-id="element-260" data-mp="gm3i4bstecy1">
                {item.description}
              </p>
            </motion.div>)}
        </motion.div>

        {/* Secondary capabilities */}
        <motion.div initial={{
        opacity: 0
      }} whileInView={{
        opacity: 1
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.5,
        delay: 0.2
      }} className="mt-12 lg:mt-16 border-t border-gray-800 pt-10" data-id="element-261" data-mp="7mzxh3e01phf">
          <h3 className="text-xs font-semibold uppercase tracking-widest text-gray-500 mb-5" data-id="element-262" data-mp="1o0q7eqrsv7yz">
            Also in every kitty
          </h3>
          <ul className="flex flex-wrap gap-2.5" data-id="element-263" data-mp="1s1iebdd790s3">
            {alsoIncluded.map(item => <li key={item} className="rounded-full border border-gray-800 bg-gray-900/60 px-4 py-2 text-sm text-gray-300" data-id="element-264" data-mp="3ckvzoa1gwfp0">
                {item}
              </li>)}
          </ul>
        </motion.div>
      </div>
    </section>;
}